import React, { useState, useEffect } from 'react';
import { Box, Alert, Chip } from '@mui/material';
import { ViiluDataGrid, PageHeader, PageContent, useRouter } from '@viilutalous/common';
import { expensesAPI } from './expensesAPI';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';

const EXPENSE_STATE = {
  DRAFT: 0,
  DELETED: 1,
  SUBMITTED: 2,
  APPROVED: 3,
  POSTED: 4,
  PAID: 5,
  REFUSED: 6,
};

const stateConfig = {
  [EXPENSE_STATE.DRAFT]: { label: 'draft', color: 'default' },
  [EXPENSE_STATE.DELETED]: { label: 'deleted', color: 'default' },
  [EXPENSE_STATE.SUBMITTED]: { label: 'submitted', color: 'warning' },
  [EXPENSE_STATE.APPROVED]: { label: 'approved', color: 'success' },
  [EXPENSE_STATE.POSTED]: { label: 'posted', color: 'primary' },
  [EXPENSE_STATE.PAID]: { label: 'paid', color: 'success' },
  [EXPENSE_STATE.REFUSED]: { label: 'refused', color: 'error' },
};

export default function Expenses({ tenantId }) {
  const { t } = useTranslation();
  const { navigate } = useRouter();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (tenantId) {
      fetchExpenses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const fetchExpenses = async () => {
    if (!tenantId) return;
    try {
      const response = await expensesAPI.getAll(tenantId);
      setExpenses(response.data.expenses || []);
    } catch (error) {
      console.error('Error fetching expenses:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNew = () => {
    navigate('/expenses/new');
  };

  const handleEdit = (expense) => {
    navigate(`/expenses/${expense.id}`);
  };

  const handleDelete = async (id) => {
    if (!tenantId) return;
    if (window.confirm(t('expenses.delete_confirm'))) {
      try {
        await expensesAPI.delete(tenantId, id);
        fetchExpenses();
      } catch (error) {
        console.error('Error deleting expense:', error);
      }
    }
  };

  const getContextMenuItems = (params) => {
    const result = [
      {
        name: t('common.new'),
        action: () => navigate('/expenses/new'),
        icon: '<i class="ag-icon ag-icon-plus"/>',
        shortcut: 'Ins',
      },
    ];

    if (!params.node) return result;

    const expense = params.node.data;
    result.push('separator');
    result.push({
      name: t('common.edit'),
      action: () => handleEdit(expense),
      icon: '<i class="ag-icon ag-icon-edit"/>',
      shortcut: 'Enter',
    });

    if (expense.state === EXPENSE_STATE.DRAFT) {
      result.push('separator');
      result.push({
        name: t('common.delete'),
        action: () => handleDelete(expense.id),
        icon: '<i class="ag-icon ag-icon-cancel"/>',
        cssClasses: ['ag-menu-option-text-danger'],
      });
    }

    return result;
  };

  const StatusCellRenderer = (props) => {
    const cfg = stateConfig[props.value] || stateConfig[EXPENSE_STATE.DRAFT];
    return (
      <Chip
        label={t(`expenses.state.${cfg.label}`)}
        color={cfg.color}
        size="small"
      />
    );
  };

  const columnDefs = [
    { field: 'number', headerName: t('expenses.number'), width: 100 },
    { field: 'description', headerName: t('expenses.description'), flex: 1, minWidth: 200 },
    {
      field: 'date',
      headerName: t('expenses.date'),
      width: 120,
      valueFormatter: (params) => params.value ? format(new Date(params.value), 'dd.MM.yyyy') : '',
    },
    { field: 'employee_name', headerName: t('expenses.employee'), width: 160 },
    {
      field: 'total',
      headerName: t('expenses.total'),
      width: 120,
      type: 'numericColumn',
      valueFormatter: (params) => params.value != null ? `${Number(params.value).toFixed(2)} \u20AC` : '',
    },
    {
      field: 'state',
      headerName: t('expenses.state'),
      width: 130,
      cellRenderer: StatusCellRenderer,
    },
  ];

  if (!tenantId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{t('expenses.select_tenant')}</Alert>
      </Box>
    );
  }

  return (
    <>
      <PageHeader />

      <PageContent>
        <ViiluDataGrid
          tenantId={tenantId}
          gridId="expenses-list"
          rowData={expenses}
          columnDefs={columnDefs}
          loading={loading}
          onRowDoubleClicked={(event) => navigate(`/expenses/${event.data.id}`)}
          getContextMenuItems={getContextMenuItems}
          onNew={handleNew}
          onEdit={(row) => navigate(`/expenses/${row.id}`)}
          onRefresh={fetchExpenses}
        />
      </PageContent>
    </>
  );
}
